import React, { ReactNode } from 'react'

import { Source, SourceUsage, TSourceRow } from 'types/GlobalSource'

import Box from 'components_new/atoms/Box'
import Chip from 'components_new/atoms/Chip'
import Text from 'components_new/atoms/Text'
import Tooltip from 'components_new/atoms/Tooltip'

import { getSourceCategory, SOURCE_CATEGORY_META } from 'utils/sources'

export enum ESourceUsageTab {
  USED = 'used',
  UNUSED = 'unused'
}

/**
 * A source is a connectable system, a row is one of the customer's actual
 * connections to it. Sources without connections are templates and are not
 * listed.
 */
export const getSourceRows = (sources: {
  [id: string]: Source
}): TSourceRow[] => {
  return Object.values(sources)
    .filter((source) => source.included && source.included.active_sources)
    .flatMap((source) => {
      const category = getSourceCategory(source.attributes.type)

      return source.included.active_sources.map((activeSource) => ({
        category,
        columnCount: activeSource.attributes.column_count ?? null,
        href: `/data-platform/collect/${SOURCE_CATEGORY_META[category].path}/${activeSource.id}/data`,
        id: activeSource.id,
        inUse: Boolean(activeSource.attributes.in_use),
        name: activeSource.attributes.display_name || source.attributes.name,
        rowCount: activeSource.attributes.row_count ?? null,
        tableCount: activeSource.attributes.table_count ?? null,
        usage: (activeSource.included && activeSource.included.usage) || []
      }))
    })
}

/**
 * Usage is the ontology tables that read from the source. Far more of them than
 * fits a cell, so the cell only says how many and the tooltip carries the list,
 * grouped by ontology.
 */
const USAGE_GROUPS: { type: SourceUsage['ontology_type']; label: string }[] = [
  { type: 'STANDARD_ONTOLOGY', label: 'SIM' },
  { type: 'CUSTOM_ONTOLOGY', label: 'Anpassad' }
]

const renderUsageTooltip = (usage: SourceUsage[]) =>
  USAGE_GROUPS.map(({ type, label }) => {
    const tables = usage.filter((item) => item.ontology_type === type)

    if (tables.length === 0) {
      return null
    }

    return (
      <Box key={type} sx={{ '&:not(:first-of-type)': { mt: 1 } }}>
        <Text color="inherit" fontWeight="bold" variant="caption">
          {label}
        </Text>
        {tables.map((item) => (
          <Text
            color="inherit"
            component="div"
            key={item.table_id}
            variant="caption"
          >
            {item.table_name}
          </Text>
        ))}
      </Box>
    )
  })

export const renderUsageCell = (usage: SourceUsage[]): ReactNode => {
  if (usage.length === 0) {
    return <Chip label="Används ej" size="xsmall" />
  }

  return (
    <Tooltip title={<Box>{renderUsageTooltip(usage)}</Box>}>
      <Box sx={{ display: 'inline-flex' }}>
        <Chip
          color="primary"
          label={`Används (${usage.length})`}
          size="xsmall"
        />
      </Box>
    </Tooltip>
  )
}

/** Not every source reports a table, attribute or row count. */
export const formatCount = (value: number | null): string =>
  value === null ? '-' : String(value)
